import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import useThema from '../hooks/useThema';

const STATUS_COR = {
  ativo:     { bg: '#f0fdf4', cor: '#16a34a', borda: '#86efac' },
  trial:     { bg: '#eff6ff', cor: '#2563eb', borda: '#93c5fd' },
  suspenso:  { bg: '#fff7ed', cor: '#ea580c', borda: '#fdba74' },
  cancelado: { bg: '#fef2f2', cor: '#dc2626', borda: '#fca5a5' },
};

const STATUS_LABEL = { ativo: 'Ativo', trial: 'Trial', suspenso: 'Suspenso', cancelado: 'Cancelado' };

const ACAO_LABEL = {
  mudar_plano: 'Mudança de plano',
  suspender:   'Suspensão',
  reativar:    'Reativação',
  impersonar:  'Impersonação',
};

function fmt(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtData(d) {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : d instanceof Date ? d.toISOString() : String(d);
  const [y, m, dia] = s.split('T')[0].split('-');
  if (!y || !m || !dia) return '—';
  return `${dia}/${m}/${y}`;
}

function Badge({ status }) {
  const s = STATUS_COR[status] || STATUS_COR.suspenso;
  return (
    <span style={{ background: s.bg, color: s.cor, border: `1px solid ${s.borda}`, borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function Card({ titulo, valor, sub, cor }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, padding: '20px 24px', flex: '1 1 160px' }}>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>{titulo}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: cor || 'var(--text-primary)' }}>{valor}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Modal detalhe do tenant ─────────────────────────────────────────────────
function ModalTenant({ tenantId, onFechar, onAtualizar }) {
  const [dados, setDados] = useState(null);
  const [acao, setAcao] = useState(null); // 'plano' | 'status'
  const [novoPlanoId, setNovoPlanoId] = useState('');
  const [precoCustomizado, setPrecoCustomizado] = useState('');
  const [novoStatus, setNovoStatus] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [enviandoDunning, setEnviandoDunning] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgTipo, setMsgTipo] = useState('erro'); // 'sucesso' | 'erro'

  function setMsgOk(texto) { setMsg(texto); setMsgTipo('sucesso'); }
  function setMsgErro(texto) { setMsg(texto); setMsgTipo('erro'); }

  const carregar = useCallback(async () => {
    try {
      const r = await api.get(`/superadmin/tenants/${tenantId}`);
      setDados(r.data);
      setNovoPlanoId(String(r.data.tenant.planoid || ''));
      setNovoStatus(r.data.tenant.status);
      setPrecoCustomizado(r.data.tenant.preco_customizado != null ? String(r.data.tenant.preco_customizado) : '');
    } catch { setMsg('Erro ao carregar tenant.'); }
  }, [tenantId]);

  useEffect(() => { carregar(); }, [carregar]);

  async function salvarPlano() {
    setSalvando(true); setMsg('');
    try {
      const preco = precoCustomizado.trim() === '' ? null : parseFloat(precoCustomizado.replace(',', '.'));
      await api.put(`/superadmin/tenants/${tenantId}/plano`, { planoid: novoPlanoId, preco_customizado: preco });
      setMsgOk('Plano alterado com sucesso!');
      setAcao(null); carregar(); onAtualizar();
    } catch (err) { setMsgErro(err.response?.data?.mensagem || 'Erro ao alterar plano.'); }
    setSalvando(false);
  }

  async function salvarStatus() {
    setSalvando(true); setMsg('');
    try {
      await api.put(`/superadmin/tenants/${tenantId}/status`, { status: novoStatus });
      setMsgOk('Status alterado com sucesso!');
      setAcao(null); carregar(); onAtualizar();
    } catch (err) { setMsgErro(err.response?.data?.mensagem || 'Erro ao alterar status.'); }
    setSalvando(false);
  }

  async function impersonar() {
    setSalvando(true); setMsg('');
    try {
      const r = await api.post(`/superadmin/tenants/${tenantId}/impersonar`);
      const url = `${window.location.origin}${window.location.pathname}?token=${r.data.token}#dashboard`;
      window.open(url, '_blank');
      setMsgOk('Sessão de impersonação aberta em nova aba. Token válido por 1 hora.');
    } catch (err) { setMsgErro(err.response?.data?.mensagem || 'Erro ao impersonar.'); }
    setSalvando(false);
  }

  async function testarDunning() {
    setEnviandoDunning(true); setMsg('');
    try {
      const r = await api.post(`/superadmin/tenants/${tenantId}/testar-dunning`);
      setMsgOk(r.data.mensagem);
    } catch (err) {
      setMsgErro(err.response?.data?.mensagem || 'Erro ao enviar e-mail de teste.');
    } finally {
      setEnviandoDunning(false);
    }
  }

  if (!dados) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, minWidth: 300, textAlign: 'center' }}>
        <p style={{ color: 'var(--text-secondary)' }}>{msg || 'Carregando...'}</p>
        {msg && <button className="btn-secondary" style={{ marginTop: 16 }} onClick={onFechar}>Fechar</button>}
      </div>
    </div>
  );

  const { tenant, usuarios, planos } = dados;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 32, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0 }}>{tenant.nome_empresa}</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>{tenant.email_admin}</p>
          </div>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <Badge status={tenant.status} />
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Plano: <strong style={{ textTransform: 'capitalize' }}>{tenant.plano}</strong> — {fmt(tenant.preco_customizado != null ? tenant.preco_customizado : tenant.preco)}/mês
            {tenant.preco_customizado != null && <span style={{ fontSize: 11, opacity: 0.7 }}> (customizado)</span>}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Vence: <strong>{fmtData(tenant.data_vencimento)}</strong>
          </span>
        </div>

        {msg && (
          <div style={{ background: msgTipo === 'sucesso' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${msgTipo === 'sucesso' ? '#86efac' : '#fca5a5'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: msgTipo === 'sucesso' ? '#16a34a' : '#dc2626' }}>
            {msg}
          </div>
        )}

        {/* Ações */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
          <button className="btn-secondary" style={{ fontSize: 13 }} onClick={() => { setAcao(acao === 'plano' ? null : 'plano'); setMsg(''); }}>
            ✏️ Alterar plano
          </button>
          <button className="btn-secondary" style={{ fontSize: 13 }} onClick={() => { setAcao(acao === 'status' ? null : 'status'); setMsg(''); }}>
            🔄 Alterar status
          </button>
          <button className="btn-primary" style={{ fontSize: 13 }} onClick={impersonar} disabled={salvando}>
            🔑 Impersonar
          </button>
          <button className="btn-secondary" style={{ fontSize: 13 }} onClick={testarDunning} disabled={enviandoDunning}>
            {enviandoDunning ? '⏳ Enviando...' : '📧 Testar e-mail dunning'}
          </button>
        </div>

        {acao === 'plano' && (
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Selecionar novo plano</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <select value={novoPlanoId} onChange={e => setNovoPlanoId(e.target.value)} style={{ flex: 1 }}>
                {planos.map(p => (
                  <option key={p.planoid} value={p.planoid}>{p.nome.charAt(0).toUpperCase() + p.nome.slice(1)} — {fmt(p.preco)}/mês</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Preço customizado (opcional)"
                value={precoCustomizado}
                onChange={e => setPrecoCustomizado(e.target.value)}
                style={{ width: 200 }}
              />
              <button className="btn-primary" style={{ fontSize: 13 }} onClick={salvarPlano} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Confirmar'}
              </button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
              Deixe em branco pra usar o preço de catálogo do plano. Use pra venda assistida do Diamante (valor negociado).
            </div>
          </div>
        )}

        {acao === 'status' && (
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Selecionar novo status</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <select value={novoStatus} onChange={e => setNovoStatus(e.target.value)} style={{ flex: 1 }}>
                <option value="ativo">Ativo</option>
                <option value="trial">Trial</option>
                <option value="suspenso">Suspenso</option>
                <option value="cancelado">Cancelado</option>
              </select>
              <button className="btn-primary" style={{ fontSize: 13 }} onClick={salvarStatus} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        )}

        {/* Usuários do tenant */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>Usuários ({usuarios.length})</div>
          {usuarios.map(u => (
            <div key={u.usuarioid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-color)', fontSize: 13 }}>
              <div>
                <span style={{ fontWeight: 600 }}>{u.nome}</span>
                <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{u.email}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{u.role}</span>
                {!u.ativo && <span style={{ fontSize: 11, color: '#dc2626' }}>inativo</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const TIPO_AVISO = {
  manutencao: { label: 'Manutenção',  bg: '#fffbeb', cor: '#92400e', borda: '#fde68a', icone: '🔧' },
  atualizacao: { label: 'Atualização', bg: '#f0fdf4', cor: '#065f46', borda: '#86efac', icone: '🚀' },
  info:        { label: 'Informação',  bg: '#eff6ff', cor: '#1e40af', borda: '#93c5fd', icone: 'ℹ️' },
};

function AbaAvisos() {
  const FORM_VAZIO = { tipo: 'manutencao', mensagem: '', data_inicio: '', data_fim: '' };
  const [avisos, setAvisos] = useState([]);
  const [form, setForm] = useState(FORM_VAZIO);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [modalLogout, setModalLogout] = useState(false);
  const [derrubando, setDerrubando] = useState(false);

  const carregar = useCallback(async () => {
    try { const r = await api.get('/superadmin/avisos'); setAvisos(r.data); } catch {}
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  async function salvar() {
    if (!form.mensagem.trim() || !form.data_inicio || !form.data_fim) return;
    setSalvando(true);
    // datetime-local não tem fuso — converte para ISO com offset do browser antes de enviar
    const payload = {
      ...form,
      data_inicio: new Date(form.data_inicio).toISOString(),
      data_fim:    new Date(form.data_fim).toISOString(),
    };
    try {
      if (editando) {
        await api.put(`/superadmin/avisos/${editando}`, payload);
      } else {
        await api.post('/superadmin/avisos', payload);
      }
      setForm(FORM_VAZIO); setEditando(null); carregar();
    } catch {} finally { setSalvando(false); }
  }

  async function remover(id) {
    if (!window.confirm('Remover este aviso?')) return;
    await api.delete(`/superadmin/avisos/${id}`);
    carregar();
  }

  function toLocalInput(dateStr) {
    const d = new Date(dateStr);
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  function iniciarEdicao(a) {
    setEditando(a.id);
    setForm({
      tipo: a.tipo,
      mensagem: a.mensagem,
      data_inicio: toLocalInput(a.data_inicio),
      data_fim:    toLocalInput(a.data_fim),
    });
  }

  function statusAviso(a) {
    const agr = new Date();
    const ini = new Date(a.data_inicio);
    const fim = new Date(a.data_fim);
    if (agr < ini) return { label: 'Agendado', bg: '#eff6ff', cor: '#2563eb' };
    if (agr > fim) return { label: 'Expirado', bg: '#f9fafb', cor: '#9ca3af' };
    return { label: 'Ativo agora', bg: '#f0fdf4', cor: '#16a34a' };
  }

  async function confirmarLogout() {
    setDerrubando(true);
    try {
      await api.post('/superadmin/forcar-logout');
      setModalLogout(false);
    } catch {} finally { setDerrubando(false); }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0 }}>📢 Avisos do Sistema</h2>
        <button
          onClick={() => setModalLogout(true)}
          style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
        >
          🔴 Derrubar todos os usuários
        </button>
      </div>

      {modalLogout && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 14, padding: '32px 36px', maxWidth: 420, width: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>⚠️ Confirmar encerramento de sessões</h3>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 24px' }}>
              Todos os usuários serão deslogados automaticamente na próxima ação que fizerem no sistema. Use após um deploy para garantir que todos estejam na versão mais recente.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setModalLogout(false)} disabled={derrubando}>Cancelar</button>
              <button
                onClick={confirmarLogout}
                disabled={derrubando}
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                {derrubando ? 'Encerrando...' : 'Sim, derrubar todos'}
              </button>
            </div>
          </div>
        </div>
      )}

      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
        Avisos aparecem como banner fixo no topo da tela para todos os usuários logados dentro da janela de data/hora configurada.
      </p>

      {/* Formulário */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, padding: '20px 24px', marginBottom: 28 }}>
        <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 15 }}>{editando ? 'Editar aviso' : 'Novo aviso'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Tipo</label>
            <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={{ marginBottom: 0, width: '100%' }}>
              {Object.entries(TIPO_AVISO).map(([v, t]) => (
                <option key={v} value={v}>{t.icone} {t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Mensagem</label>
            <textarea
              value={form.mensagem}
              onChange={e => setForm(f => ({ ...f, mensagem: e.target.value }))}
              placeholder="Ex: Haverá atualização do sistema hoje às 20h. Favor salvar seu trabalho antes desse horário."
              rows={3}
              style={{ width: '100%', resize: 'vertical', marginBottom: 0, boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Exibir a partir de</label>
            <input type="datetime-local" value={form.data_inicio} onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))} style={{ marginBottom: 0, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Ocultar em</label>
            <input type="datetime-local" value={form.data_fim} onChange={e => setForm(f => ({ ...f, data_fim: e.target.value }))} style={{ marginBottom: 0, width: '100%', boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-primary" onClick={salvar} disabled={salvando || !form.mensagem.trim() || !form.data_inicio || !form.data_fim}>
            {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Publicar aviso'}
          </button>
          {editando && (
            <button className="btn-secondary" onClick={() => { setEditando(null); setForm(FORM_VAZIO); }}>Cancelar</button>
          )}
        </div>
      </div>

      {/* Lista */}
      {avisos.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Nenhum aviso cadastrado.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {avisos.map(a => {
            const t = TIPO_AVISO[a.tipo] || TIPO_AVISO.info;
            const st = statusAviso(a);
            return (
              <div key={a.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 10, padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ background: t.bg, color: t.cor, border: `1px solid ${t.borda}`, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{t.icone} {t.label}</span>
                      <span style={{ background: st.bg, color: st.cor, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{st.label}</span>
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.5 }}>{a.mensagem}</p>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      De {new Date(a.data_inicio).toLocaleString('pt-BR')} até {new Date(a.data_fim).toLocaleString('pt-BR')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => iniciarEdicao(a)}>Editar</button>
                    <button className="btn-danger"    style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => remover(a.id)}>Remover</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Página principal ────────────────────────────────────────────────────────
function SuperAdmin({ onLogout }) {
  const { tema, alternarTema } = useThema();
  const [aba, setAba] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [logs, setLogs] = useState([]);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [tenantSelecionado, setTenantSelecionado] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const carregarDashboard = useCallback(async () => {
    try {
      const r = await api.get('/superadmin/dashboard');
      setDashboard(r.data);
    } catch {}
  }, []);

  const carregarTenants = useCallback(async () => {
    try {
      const r = await api.get('/superadmin/tenants');
      setTenants(r.data);
    } catch {}
  }, []);

  const carregarLogs = useCallback(async () => {
    try {
      const r = await api.get('/superadmin/logs');
      setLogs(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    setCarregando(true);
    Promise.all([carregarDashboard(), carregarTenants(), carregarLogs()])
      .finally(() => setCarregando(false));
  }, [carregarDashboard, carregarTenants, carregarLogs]);

  const tenantsFiltrados = tenants.filter(t => {
    const matchStatus = filtroStatus === 'todos' || t.status === filtroStatus;
    const matchBusca = !busca || t.nome_empresa.toLowerCase().includes(busca.toLowerCase()) || t.email_admin.toLowerCase().includes(busca.toLowerCase());
    return matchStatus && matchBusca;
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Header */}
      <div style={{ background: '#0d1b2a', padding: '16px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: '#b8d8f0', fontWeight: 700, fontSize: 18 }}>CronoObra</span>
          <span style={{ background: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6 }}>SUPER ADMIN</span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={alternarTema} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '6px 12px', fontSize: 16, cursor: 'pointer' }}>
            {tema === 'dark' ? '☀️' : '🌙'}
          </button>
          <button onClick={onLogout} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
            Sair
          </button>
        </div>
      </div>

      {/* Abas */}
      <div style={{ borderBottom: '1px solid var(--border-color)', padding: '0 28px', display: 'flex', gap: 0 }}>
        {[['dashboard', 'Dashboard'], ['tenants', 'Tenants'], ['avisos', '📢 Avisos'], ['logs', 'Logs de Auditoria']].map(([id, label]) => (
          <button key={id} onClick={() => setAba(id)} style={{
            padding: '14px 20px', background: 'none', border: 'none', borderBottom: aba === id ? '2px solid #7c3aed' : '2px solid transparent',
            color: aba === id ? '#7c3aed' : 'var(--text-secondary)', fontWeight: aba === id ? 700 : 400,
            fontSize: 14, cursor: 'pointer',
          }}>
            {label}
            {id === 'tenants' && tenants.length > 0 && (
              <span style={{ marginLeft: 6, background: 'var(--bg-secondary)', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{tenants.length}</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ padding: 28 }}>
        {carregando ? (
          <p style={{ color: 'var(--text-secondary)' }}>Carregando...</p>
        ) : (
          <>
            {/* ── Dashboard ── */}
            {aba === 'dashboard' && dashboard && (
              <div>
                <h2 style={{ marginBottom: 24 }}>Visão geral</h2>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
                  <Card titulo="MRR (Receita Recorrente)" valor={fmt(dashboard.mrr)} sub="Soma dos planos ativos" cor="#7c3aed" />
                  <Card titulo="Tenants Ativos" valor={dashboard.ativos} cor="#16a34a" />
                  <Card titulo="Em Trial" valor={dashboard.trials} cor="#2563eb" />
                  <Card titulo="Suspensos" valor={dashboard.suspensos} cor="#ea580c" />
                  <Card titulo="Cancelados" valor={dashboard.cancelados} cor="#dc2626" />
                </div>

                <h3 style={{ marginBottom: 16 }}>Últimas ações</h3>
                {logs.slice(0, 10).length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Nenhuma ação registrada ainda.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {logs.slice(0, 10).map(log => (
                      <div key={log.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                        <div>
                          <strong>{ACAO_LABEL[log.acao] || log.acao}</strong>
                          {log.nome_empresa && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>→ {log.nome_empresa}</span>}
                          {log.detalhes && Object.keys(log.detalhes).length > 0 && (
                            <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
                              ({Object.entries(log.detalhes).map(([k, v]) => `${k}: ${v}`).join(', ')})
                            </span>
                          )}
                        </div>
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(log.created_at).toLocaleString('pt-BR')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Tenants ── */}
            {aba === 'tenants' && (
              <div>
                <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    placeholder="Buscar por empresa ou e-mail..."
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    style={{ flex: '1 1 240px', maxWidth: 360 }}
                  />
                  <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                    <option value="todos">Todos os status</option>
                    <option value="ativo">Ativos</option>
                    <option value="trial">Trial</option>
                    <option value="suspenso">Suspensos</option>
                    <option value="cancelado">Cancelados</option>
                  </select>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{tenantsFiltrados.length} resultado{tenantsFiltrados.length !== 1 ? 's' : ''}</span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                        {['Empresa', 'E-mail', 'Plano', 'Status', 'Vencimento', 'Obras', 'Usuários', ''].map(h => (
                          <th key={h} style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tenantsFiltrados.map(t => (
                        <tr key={t.tenantid} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '12px 12px', fontWeight: 600 }}>{t.nome_empresa}</td>
                          <td style={{ padding: '12px 12px', color: 'var(--text-secondary)' }}>{t.email_admin}</td>
                          <td style={{ padding: '12px 12px', textTransform: 'capitalize' }}>{t.plano}</td>
                          <td style={{ padding: '12px 12px' }}><Badge status={t.status} /></td>
                          <td style={{ padding: '12px 12px', color: 'var(--text-secondary)' }}>{fmtData(t.data_vencimento)}</td>
                          <td style={{ padding: '12px 12px', textAlign: 'center' }}>{t.obras_ativas}</td>
                          <td style={{ padding: '12px 12px', textAlign: 'center' }}>{t.usuarios_ativos}</td>
                          <td style={{ padding: '12px 12px' }}>
                            <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setTenantSelecionado(t.tenantid)}>
                              Detalhes
                            </button>
                          </td>
                        </tr>
                      ))}
                      {tenantsFiltrados.length === 0 && (
                        <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum tenant encontrado.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Avisos ── */}
            {aba === 'avisos' && <AbaAvisos />}

            {/* ── Logs ── */}
            {aba === 'logs' && (
              <div>
                <h2 style={{ marginBottom: 20 }}>Log de auditoria</h2>
                {logs.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Nenhuma ação registrada.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {logs.map(log => (
                      <div key={log.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 10, padding: '14px 18px', fontSize: 13 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <strong>{ACAO_LABEL[log.acao] || log.acao}</strong>
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(log.created_at).toLocaleString('pt-BR')}</span>
                        </div>
                        {log.nome_empresa && <div style={{ color: 'var(--text-secondary)' }}>Tenant: <strong>{log.nome_empresa}</strong></div>}
                        {log.detalhes && Object.keys(log.detalhes).length > 0 && (
                          <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                            {Object.entries(log.detalhes).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {tenantSelecionado && (
        <ModalTenant
          tenantId={tenantSelecionado}
          onFechar={() => setTenantSelecionado(null)}
          onAtualizar={() => { carregarTenants(); carregarDashboard(); carregarLogs(); }}
        />
      )}
    </div>
  );
}

export default SuperAdmin;
